import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { debounce, xor } from 'lodash';
import { getCompanies } from '../../redux/actions/companies';

import {
  Content,
  Form,
  Notification,
  CompanyCreateForm,
  CompanyList,
  RouteWrapper,
  HeadingSection,
  Section,
  Pagination,
} from '../../components';

import PAGINATE from '../../data/paginate';

interface Props {
  companies: Array<any>;
  isFetching: boolean;
  dispatch: Function;
}

interface State {
  page: number;
  search: string;
  isMergingCompanies: boolean;
  mergeCompaniesList: number[];
  isFetching: boolean;
  isAddingCompany: boolean;
  error: string | boolean;
}

class Route extends React.Component<Props, State> {
  state = {
    page: 0,
    search: '',
    isMergingCompanies: false,
    mergeCompaniesList: [] as number[],
    isFetching: false,
    isAddingCompany: false,
    error: false,
  };

  static MAX_COMPANIES = 2;

  componentDidMount() {
    this.props.dispatch(getCompanies());
  }

  toggleCompany(company) {
    if (
      this.state.mergeCompaniesList.length >= Route.MAX_COMPANIES &&
      this.state.mergeCompaniesList.indexOf(company.id) === -1
    ) {
      return;
    }
    this.setState({
      mergeCompaniesList: xor(this.state.mergeCompaniesList, [company.id]),
    });
  }

  mergeCompanies = async () => {
    this.props.dispatch(
      push(
        `/companies/${this.state.mergeCompaniesList[0]}/merge/${
          this.state.mergeCompaniesList[1]
        }`,
      ),
    );
  };

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_COMPANIES"
        title="Company"
        loading={this.props.isFetching}
      >
        <HeadingSection
          title="Companies"
          controls={[
            <button
              key="add"
              className={`is-${
                this.state.isAddingCompany ? 'primary' : 'default'
              }`}
              onClick={() =>
                this.setState({ isAddingCompany: !this.state.isAddingCompany })
              }
            >
              Add Company
            </button>,
            <button
              key="merge"
              className={`is-${
                this.state.isMergingCompanies ? 'primary' : 'default'
              }`}
              onClick={() => {
                this.setState({
                  isMergingCompanies: !this.state.isMergingCompanies,
                  mergeCompaniesList: [],
                });
              }}
            >
              Merge Companies
            </button>,
          ]}
        />
        <Section>
          <Content>
            {this.state.isAddingCompany ? (
              <CompanyCreateForm
                showHeading={true}
                isTypeForm={false}
                onUpdate={async (response) => {
                  await this.props.dispatch(getCompanies());
                  this.setState({ isAddingCompany: false });
                }}
              />
            ) : (
              <>
                {this.state.isMergingCompanies && (
                  <Notification type="default">
                    <p>Select the companies you want to merge</p>
                    {this.state.mergeCompaniesList.length > 1 && (
                      <button
                        onClick={this.mergeCompanies}
                        className="is-primary"
                      >
                        Merge
                      </button>
                    )}
                  </Notification>
                )}
                <div className="field">
                  <Form
                    type="search"
                    fields={[{ name: 'search', type: 'text' }]}
                    hasButton={false}
                    handleChange={debounce(({ search }) => {
                      this.setState({ search, page: 0 }, () =>
                        this.props.dispatch(getCompanies(undefined, 0, search)),
                      );
                    }, 200)}
                  />
                </div>
                {(!this.props.isFetching ||
                  this.props.companies.length > 0) && (
                  <>
                    <div className="field">
                      <CompanyList
                        hideSearch
                        handleClick={(company) => {
                          if (this.state.isMergingCompanies) {
                            this.toggleCompany(company);
                            return;
                          }
                          this.props.dispatch(push(`/companies/${company.id}`));
                        }}
                        isSelected={(id) =>
                          this.state.mergeCompaniesList.indexOf(id) !== -1
                        }
                        items={this.props.companies.slice(
                          0,
                          PAGINATE.companies.MAX,
                        )}
                      />
                    </div>
                    <div className="field">
                      <Pagination
                        page={this.state.page}
                        lastPage={
                          this.props.companies.length <= PAGINATE.companies.MAX
                        }
                        handleUpdate={async (page) => {
                          this.setState({ page });
                          await this.props.dispatch(
                            getCompanies(undefined, page, this.state.search),
                          );
                        }}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </Content>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ companies }) => ({
  companies: companies.companies,
  isFetching: companies.isFetching,
});

export default connect(mapStateToProps)(Route);
